import { useSubpantalla } from '../ui/navegacionReact';
import { useEffect, useState } from 'react';
import { Users, Clock, Download } from 'lucide-react';
import { api } from '../api';
import ListaEstandar from '../ui/ListaEstandar';
import EstadoVacio from '../ui/EstadoVacio';
import TiempoRelativo from '../ui/TiempoRelativo';
import NotaExplicativa from '../ui/NotaExplicativa';
import { Cargando } from '../ui/EstadoDePantalla';

/* =====================================================================
   PRESENCIA · Accesos Fase 5
   =====================================================================
   TRES PESTAÑAS: quién está ahora, la asistencia por día, y los
   rechazos.

   LOS RECHAZOS EN SU PROPIA PESTAÑA y no mezclados con la asistencia:
   una tarjeta revocada probando a las tres de la mañana es información,
   y perderla entre trescientos fichajes buenos es no tenerla.

   SIN VERDE. Gris para lo normal y ámbar para «entró hace más de doce
   horas y no ha salido» — que casi siempre es alguien que olvidó fichar
   la salida. Pintar de verde «está aquí» sería afirmar algo que TEGO no
   sabe: sabe que fichó.

   Y LA FRASE DE LA NOMINA ESTA EN TODAS PARTES, incluida la respuesta
   del servidor y el CSV. No es un descargo legal: es que sin ella la
   primera pregunta que hace todo el mundo al ver «horas de presencia»
   es si eso paga.
   ===================================================================== */
const HOY = () => new Date().toISOString().slice(0, 10);
const HACE = (dias) =>
  new Date(Date.now() - dias * 86400000).toISOString().slice(0, 10);

export default function Presencia() {
  const [pestana, setPestana] = useState('ahora');
  useSubpantalla('pestana', pestana !== 'ahora', () => setPestana('ahora'), { valor: pestana, abrir: setPestana, enUrl: true, reemplaza: true });

  return (
    <div className="space-y-4">
      <div>
        {/* ANEXO-CLIENTE-01 H-3: el título dice qué es; el subtítulo, para qué sirve. */}
        <h2 className="text-xl font-semibold text-gray-900">Entradas y salidas</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Quién está dentro, desde cuándo y por qué puerta.
        </p>
      </div>

      <NotaExplicativa titulo="Esto es control de entradas y salidas, no nómina">
        TEGO apunta a qué hora ficha cada persona y suma las horas entre su
        primera entrada y su última salida del día. <strong>No calcula
        pago</strong>: ni descuenta comida, ni aplica recargos por domingo,
        ni redondea. Si tu contadora necesita eso, esta lista es el dato de
        partida, no el resultado.
      </NotaExplicativa>

      <div role="tablist" aria-label="Qué ver" className="flex flex-wrap gap-2">
        {[
          { id: 'ahora', titulo: 'Quién está ahora' },
          { id: 'asistencia', titulo: 'Asistencia' },
          { id: 'rechazos', titulo: 'Rechazos' },
        ].map((op) => (
          <button
            key={op.id} type="button" role="tab"
            aria-selected={pestana === op.id}
            onClick={() => setPestana(op.id)}
            className={`rounded-lg border px-4 py-2 text-sm font-semibold ${
              pestana === op.id
                ? 'border-[var(--color-marca)] bg-blue-50 text-gray-900'
                : 'border-gray-200 text-gray-700 hover:border-gray-400'}`}
          >
            {op.titulo}
          </button>
        ))}
      </div>

      {pestana === 'ahora' && <Ahora />}
      {pestana === 'asistencia' && <Asistencia />}
      {pestana === 'rechazos' && <Rechazos />}
    </div>
  );
}


function Ahora() {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let vigente = true;
    const pedir = () => api.get('/presencia/ahora')
      .then((d) => vigente && setDatos(d))
      .catch((e) => vigente && setError(e.message));
    pedir();
    // SE REFRESCA SOLO CADA MINUTO. Este tablero se mira en una pantalla
    // colgada en la oficina, y uno que hay que recargar a mano deja de
    // mirarse el segundo día.
    const t = setInterval(pedir, 60000);
    return () => { vigente = false; clearInterval(t); };
  }, []);

  if (error) return <p role="alert" className="text-sm text-red-700">{error}</p>;
  if (!datos) return <Cargando filas={4} />;

  if (datos.cuantos === 0) {
    return (
      <EstadoVacio
        icono={Users}
        titulo="No hay nadie dentro"
        descripcion="Nadie ha fichado entrada hoy, o todos han fichado ya su salida."
      />
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-700">
        <strong>{datos.cuantos}</strong> persona{datos.cuantos === 1 ? '' : 's'} dentro.
      </p>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {datos.dentro.map((p) => (
          <div key={p.id}
               className={`rounded-xl border p-3 ${
                 p.lleva_demasiado
                   ? 'border-amber-300 bg-amber-50'
                   : 'border-gray-200 bg-white'}`}>
            <p className="font-semibold text-gray-900">{p.nombre_completo}</p>
            <p className="text-xs text-gray-500">
              {p.ubicacion_nombre || p.ubicacion || 'Sin ubicación'}
            </p>
            <p className="mt-1 inline-flex items-center gap-1 text-sm text-gray-700">
              <Clock className="h-3.5 w-3.5" aria-hidden="true" />
              desde <TiempoRelativo valor={p.desde} />
            </p>
            {p.lleva_demasiado && (
              /* AMBAR, NO ROJO, Y CON SU EXPLICACION: casi siempre es
                 alguien que olvidó fichar la salida, no alguien que
                 lleva doce horas dentro. */
              <p className="mt-1 text-xs text-amber-800">
                Lleva más de 12 horas sin fichar salida. Lo más probable es
                que se le olvidara al irse.
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function Asistencia() {
  const [desde, setDesde] = useState(HACE(7));
  const [hasta, setHasta] = useState(HOY());
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let vigente = true;
    api.get(`/presencia/asistencia?desde=${desde}&hasta=${hasta}`)
      .then((d) => vigente && setDatos(d))
      .catch((e) => vigente && setError(e.message));
    return () => { vigente = false; };
  }, [desde, hasta]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label htmlFor="desde" className="block text-xs font-semibold text-gray-700 mb-1">Desde</label>
          <input id="desde" type="date" value={desde}
                 onChange={(e) => setDesde(e.target.value)}
                 className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label htmlFor="hasta" className="block text-xs font-semibold text-gray-700 mb-1">Hasta</label>
          <input id="hasta" type="date" value={hasta}
                 onChange={(e) => setHasta(e.target.value)}
                 className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </div>
        {/* EL CSV LO ARMA EL SERVIDOR: son datos personales de la
            plantilla entera y no tienen por qué pasar por la memoria de
            una pestaña para volver a bajar. */}
        <a
          href={`/api/presencia/asistencia.csv?desde=${desde}&hasta=${hasta}`}
          className="inline-flex items-center gap-1.5 border border-gray-300 text-gray-800 text-sm font-semibold px-3 py-2 rounded-lg hover:bg-gray-50"
        >
          <Download className="h-4 w-4" /> Bajar para Excel
        </a>
      </div>

      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      {datos && datos.filas.length === 0 && (
        <EstadoVacio
          icono={Clock}
          titulo="Nadie fichó en esas fechas"
          descripcion="Prueba con otro rango, o comprueba que el punto de fichaje esté conectado."
        />
      )}
      {datos && datos.filas.length > 0 && (
        <>
          <ListaEstandar
            titulo="Asistencia"
            subtitulo={datos.aviso}
            columnas={[
              { etiqueta: 'Persona', valor: (f) => f.nombre_completo, clase: 'text-gray-900' },
              { etiqueta: 'Día', tipo: 'fecha', valor: (f) => f.dia },
              {
                etiqueta: 'Entrada', valor: (f) => f.entrada,
                render: (f) => (f.entrada
                  ? new Date(f.entrada).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
                  : <span className="text-gray-400">—</span>),
              },
              {
                etiqueta: 'Salida', valor: (f) => f.salida,
                render: (f) => (f.salida
                  ? new Date(f.salida).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
                  /* NO SE INVENTA UNA SALIDA. Si no fichó, se dice. */
                  : <span className="text-amber-700">Sin fichar</span>),
              },
              {
                etiqueta: 'Horas de presencia', valor: (f) => f.horas,
                render: (f) => (f.horas != null
                  ? `${Number(f.horas).toFixed(2)} h`
                  : <span className="text-gray-400">—</span>),
              },
            ]}
            filas={datos.filas}
            exportar={{ nombre: 'asistencia' }}
            claveFila={(f) => `${f.id_usuario}-${f.dia}`}
          />
        </>
      )}
    </div>
  );
}


function Rechazos() {
  const [filas, setFilas] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let vigente = true;
    api.get('/presencia/rechazos')
      .then((d) => vigente && setFilas(d))
      .catch((e) => vigente && setError(e.message));
    return () => { vigente = false; };
  }, []);

  const TEXTO = {
    RECHAZADO_DESCONOCIDA: 'Tarjeta no dada de alta aquí',
    RECHAZADO_INACTIVA: 'Credencial retirada o persona de baja',
    RECHAZADO_CONGELADA: 'Demasiados intentos seguidos',
    FUERA_DE_HORARIO: 'Fuera de su horario (se registró igual)',
  };

  if (error) return <p role="alert" className="text-sm text-red-700">{error}</p>;
  if (filas.length === 0) {
    return (
      <EstadoVacio
        icono={Users}
        titulo="Nada que revisar"
        descripcion="No se ha rechazado ninguna lectura en los últimos 30 días."
      />
    );
  }

  return (
    <ListaEstandar
      titulo="Lecturas rechazadas"
      subtitulo="Se guardan a propósito: una tarjeta retirada probando de madrugada es información, no ruido."
      columnas={[
        { etiqueta: 'Cuándo', tipo: 'fecha_hora', valor: (f) => f.hora_servidor,
          render: (f) => <TiempoRelativo valor={f.hora_servidor} /> },
        { etiqueta: 'Qué pasó', valor: (f) => TEXTO[f.resultado] || f.resultado },
        /* «Desconocida» significa que TEGO no supo de quién era. Poner
           «—» sin más haría pensar en un dato que falta; decirlo es el
           dato. */
        { etiqueta: 'Quién', valor: (f) => f.nombre_completo || 'No se pudo saber' },
        { etiqueta: 'Punto', valor: (f) => f.punto || '—' },
        { etiqueta: 'Ubicación', valor: (f) => f.ubicacion || '—' },
      ]}
      filas={filas}
      exportar={{ nombre: 'rechazos-de-fichaje' }}
      claveFila={(f) => f.id}
    />
  );
}
